import React, { useRef, useEffect } from 'react';
import { Building, Phone, MapPin, Instagram, Facebook, Globe, MessageCircle, Map, FileText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface ClinicInfoFormProps {
  clinicName: string;
  setClinicName: (val: string) => void;
  clinicLocation: string;
  setClinicLocation: (val: string) => void;
  clinicAddress: string;
  setClinicAddress: (val: string) => void;
  clinicPhoneFixed: string;
  setClinicPhoneFixed: (val: string) => void;
  clinicWhatsApp: string;
  setClinicWhatsApp: (val: string) => void;
  clinicMaps: string;
  setClinicMaps: (val: string) => void;
  clinicInstagram: string;
  setClinicInstagram: (val: string) => void;
  clinicFacebook: string;
  setClinicFacebook: (val: string) => void;
  clinicSite: string;
  setClinicSite: (val: string) => void;
  clinicHistory: string;
  setClinicHistory: (val: string) => void;
}

// Helper components (seguindo padrão do AssistantIdentityForm)
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => {
  const { isDark } = useTheme();
  return (
    <label className="block text-sm">
      <span className={`mb-1 block ${
        isDark ? 'text-gray-300' : 'text-gray-700'
      }`}>{label}</span>
      {children}
      {hint && <span className={`mt-1 block text-[11px] ${
        isDark ? 'text-gray-400' : 'text-gray-500'
      }`}>{hint}</span>}
    </label>
  );
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <input 
      ref={ref}
      {...props} 
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
        isDark 
          ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
          : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
      } ${props.className ?? ""}`} 
    />
  );
});

const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <textarea 
      ref={ref}
      {...props} 
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${
        isDark 
          ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
          : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
      } ${props.className ?? ""}`} 
    />
  );
});

const ClinicInfoForm: React.FC<ClinicInfoFormProps> = ({
  clinicName, setClinicName,
  clinicLocation, setClinicLocation,
  clinicAddress, setClinicAddress,
  clinicPhoneFixed, setClinicPhoneFixed,
  clinicWhatsApp, setClinicWhatsApp,
  clinicMaps, setClinicMaps,
  clinicInstagram, setClinicInstagram,
  clinicFacebook, setClinicFacebook,
  clinicSite, setClinicSite,
  clinicHistory, setClinicHistory,
}) => {
  const { isDark } = useTheme();
  
  // Refs para os inputs (funcionalidade original)
  const nameRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const phoneFixedRef = useRef<HTMLInputElement>(null);
  const whatsAppRef = useRef<HTMLInputElement>(null);
  const mapsRef = useRef<HTMLInputElement>(null);
  const instagramRef = useRef<HTMLInputElement>(null);
  const facebookRef = useRef<HTMLInputElement>(null);
  const siteRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLTextAreaElement>(null);
  
  // Inicializar os valores dos inputs (funcionalidade original)
  useEffect(() => {
    if (nameRef.current) nameRef.current.value = clinicName;
    if (locationRef.current) locationRef.current.value = clinicLocation;
    if (addressRef.current) addressRef.current.value = clinicAddress;
    if (phoneFixedRef.current) phoneFixedRef.current.value = clinicPhoneFixed;
    if (whatsAppRef.current) whatsAppRef.current.value = clinicWhatsApp;
    if (mapsRef.current) mapsRef.current.value = clinicMaps;
    if (instagramRef.current) instagramRef.current.value = clinicInstagram;
    if (facebookRef.current) facebookRef.current.value = clinicFacebook;
    if (siteRef.current) siteRef.current.value = clinicSite;
    if (historyRef.current) historyRef.current.value = clinicHistory;
  }, [clinicName, clinicLocation, clinicAddress, clinicPhoneFixed, clinicWhatsApp, 
      clinicMaps, clinicInstagram, clinicFacebook, clinicSite, clinicHistory]);
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Building className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Nome da clínica</span>
            </div>
          </label>
          <Input 
            ref={nameRef}
            type="text" 
            placeholder="Ex: Clínica Odontológica Sorrisos"
            defaultValue={clinicName}
            onBlur={() => nameRef.current && setClinicName(nameRef.current.value)}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <MapPin className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Localização</span>
            </div>
          </label>
          <Input 
            ref={locationRef}
            type="text" 
            placeholder="Ex: São Paulo, SP"
            defaultValue={clinicLocation}
            onBlur={() => locationRef.current && setClinicLocation(locationRef.current.value)}
          />
        </div>
      </div>
      
      <div>
        <label className={`block text-sm font-medium mb-1 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
          <div className="flex items-center gap-2">
            <Map className={`w-4 h-4 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <span>Endereço completo</span>
          </div>
        </label>
        <Input 
          ref={addressRef}
          type="text" 
          placeholder="Ex: Rua dos Dentes, 123 - Jardim Bucal"
          defaultValue={clinicAddress}
          onBlur={() => addressRef.current && setClinicAddress(addressRef.current.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Phone className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Telefone fixo</span>
            </div>
          </label>
          <Input 
            ref={phoneFixedRef}
            type="tel" 
            placeholder="Ex: (11) 3333-4444"
            defaultValue={clinicPhoneFixed}
            onBlur={() => phoneFixedRef.current && setClinicPhoneFixed(phoneFixedRef.current.value)}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <MessageCircle className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>WhatsApp</span>
            </div>
          </label>
          <Input 
            ref={whatsAppRef}
            type="tel" 
            placeholder="Ex: (11) 98765-4321"
            defaultValue={clinicWhatsApp}
            onBlur={() => whatsAppRef.current && setClinicWhatsApp(whatsAppRef.current.value)}
          />
        </div>
      </div>
      
      <h3 className={`font-medium mt-6 mb-3 flex items-center gap-2 ${
        isDark ? 'text-gray-200' : 'text-gray-800'
      }`}>
        <Globe className="w-5 h-5 text-sminy-primary" />
        <span>Presença Digital</span>
      </h3>
      
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-2xl border ${
        isDark 
          ? 'bg-gray-700/50 border-gray-600'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Map className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Link do Google Maps</span>
            </div>
          </label>
          <Input 
            ref={mapsRef}
            type="text" 
            placeholder="https://goo.gl/maps/..."
            defaultValue={clinicMaps}
            onBlur={() => mapsRef.current && setClinicMaps(mapsRef.current.value)}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Instagram className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Instagram (URL)</span>
            </div>
          </label>
          <Input 
            ref={instagramRef}
            type="text" 
            placeholder="https://instagram.com/..."
            defaultValue={clinicInstagram}
            onBlur={() => instagramRef.current && setClinicInstagram(instagramRef.current.value)}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Facebook className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Facebook (URL)</span>
            </div>
          </label>
          <Input 
            ref={facebookRef}
            type="text" 
            placeholder="https://facebook.com/..."
            defaultValue={clinicFacebook}
            onBlur={() => facebookRef.current && setClinicFacebook(facebookRef.current.value)}
          />
        </div>
        
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Globe className={`w-4 h-4 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <span>Site (URL)</span>
            </div>
          </label>
          <Input 
            ref={siteRef}
            type="text" 
            placeholder="https://..."
            defaultValue={clinicSite}
            onBlur={() => siteRef.current && setClinicSite(siteRef.current.value)}
          />
        </div>
      </div>
      
      <div className="mt-4">
        <label className={`block text-sm font-medium mb-1 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
          <div className="flex items-center gap-2">
            <FileText className={`w-4 h-4 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <span>História da clínica</span>
          </div>
        </label>
        <Textarea 
          ref={historyRef}
          rows={4}
          placeholder="Conte um pouco sobre a história da sua clínica..."
          defaultValue={clinicHistory}
          onBlur={() => historyRef.current && setClinicHistory(historyRef.current.value)}
        />
      </div>
    </div>
  );
};

export default ClinicInfoForm;